import { Link, NavLink, useLocation } from "react-router"
import { CollapseIcon, ExpandIcon } from "./icons"
import type { AdminRole } from "../session-context"
import { NAV_ITEMS, TEAM_NAV_ITEMS, getAccessibleNavItems } from "../navigation"

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
  role?: AdminRole | null
}

export function Sidebar({ collapsed, onToggle, role }: SidebarProps) {
  const { pathname } = useLocation()
  const navItems = getAccessibleNavItems(NAV_ITEMS, role)
  const teamNavItems = getAccessibleNavItems(TEAM_NAV_ITEMS, role)
  const contentActive =
    pathname === "/admin/content" ||
    pathname.startsWith("/admin/newsletters") ||
    pathname.startsWith("/admin/subscribers")

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-40 hidden flex-col border-r border-gold-500 bg-cream-50 transition-[width] duration-300 lg:flex ${
        collapsed ? "w-20" : "w-[305px]"
      }`}
    >
      <div className="flex h-[72px] items-center justify-between border-b border-gold-500/50 px-5">
        <Link to="/admin" className="flex items-center gap-3">
          <span className="grid h-10 w-10 flex-none place-items-center overflow-hidden rounded-full bg-forest-950">
            <img src="/images/home/logo.png" alt="" className="h-full w-full object-contain p-1.5" />
          </span>
          {!collapsed && (
            <span className="font-display text-lg font-bold text-ink">
              Redempta Admin
            </span>
          )}
        </Link>
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-gold-500 bg-white text-ink/60 transition-colors hover:bg-cream-100 hover:text-ink"
        >
          {collapsed ? <ExpandIcon className="h-4 w-4" /> : <CollapseIcon className="h-4 w-4" />}
        </button>
      </div>

      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-4" aria-label="Admin">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            title={collapsed ? item.label : undefined}
            className={({ isActive }) => {
              const active = isActive || (item.to === "/admin/content" && contentActive)
              return `flex items-center gap-3 rounded-full px-3 py-2.5 text-sm font-medium transition-colors ${
                active
                  ? "bg-forest-950 text-white"
                  : "text-ink/70 hover:bg-cream-200 hover:text-ink"
              } ${collapsed ? "justify-center" : ""}`
            }}
          >
            <item.icon className="h-5 w-5 flex-none" />
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}

        {teamNavItems.length > 0 && (
          <>
            <div className={`my-3 border-t border-gold-500/50 ${collapsed ? "mx-2" : ""}`} />
            {teamNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-full px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-forest-950 text-white"
                      : "text-ink/70 hover:bg-cream-200 hover:text-ink"
                  } ${collapsed ? "justify-center" : ""}`
                }
              >
                <item.icon className="h-5 w-5 flex-none" />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      <div className={`border-t border-gold-500/50 p-4 ${collapsed ? "px-2" : ""}`}>
        <div className={`flex items-center gap-2 text-xs text-forest-700 ${collapsed ? "justify-center" : ""}`}>
          <span className="h-2 w-2 rounded-full bg-forest-700" />
          {!collapsed && <span>Admin workspace</span>}
        </div>
      </div>
    </aside>
  )
}
